import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import SportsTennisIcon from "@mui/icons-material/SportsTennis";
import { EmptyState } from "@/core/ui";

const TYPE_LABELS = { MASC: "Masculino", FEM: "Feminino", MISTA: "Mista" };

const SeasonAccordion = ({
  season,
  ui,
  seasonResult,
  lines,
  membersCount,
  sentLines,
  lineDrafts,
  categories,
  rounds,
  onToggleSeason,
  onUpdateSeasonUi,
  onOpenFinishSeason,
  onOpenNewRound,
  onOpenManageRank,
  onScoreChange,
  lineHasChanges,
  canSendLine,
  onSendLine,
}) => (
  <Accordion
    expanded={ui.expanded}
    onChange={(_, expanded) => onToggleSeason(season.id, expanded)}
    disableGutters
    elevation={0}
    sx={{
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 2,
      "&:before": { display: "none" },
    }}
  >
    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={1}
        sx={{ width: "100%" }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {season.name}
          </Typography>
        </Stack>
        <Chip
          size="small"
          label={season.isActive ? "Em andamento" : "Encerrado"}
          color={season.isActive ? "success" : "default"}
          sx={{ fontWeight: 700 }}
        />
      </Stack>
    </AccordionSummary>

    <AccordionDetails sx={{ px: 2, pb: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 4 }}
      >
        {season.isActive && (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() => onOpenFinishSeason(season.id)}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontSize: 16,
                px: 2,
              }}
            >
              Finalizar Ranking
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={() => onOpenNewRound(season.id)}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontSize: 16,
                px: 2,
              }}
            >
              Nova rodada
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={<PeopleAltOutlinedIcon />}
              onClick={() => onOpenManageRank(season.id)}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontSize: 16,
                px: 2,
              }}
            >
              Gerenciar ranking
            </Button>
          </Stack>
        )}
      </Stack>

      <Grid container spacing={1.3} sx={{ mb: 1.2 }}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel id={`round-label-${season.id}`}>Rodada</InputLabel>
            <Select
              labelId={`round-label-${season.id}`}
              value={ui.selectedRound}
              label="Rodada"
              onChange={(event) =>
                onUpdateSeasonUi(season.id, "selectedRound", event.target.value)
              }
            >
              {rounds.length === 0 ? (
                <MenuItem value="" disabled>
                  Sem rodadas existentes
                </MenuItem>
              ) : (
                rounds.map((round) => (
                  <MenuItem key={round.id} value={round.id}>
                    Rodada {round.number}{" "}
                    {round?.createdAt
                      ? ` - Inicio: ${new Date(round.createdAt).toLocaleDateString()}`
                      : ""}{" "}
                    {round?.endsAt
                      ? ` - Fim: ${new Date(round.endsAt).toLocaleDateString()}`
                      : ""}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel id={`category-label-${season.id}`}>
              Categoria
            </InputLabel>
            <Select
              labelId={`category-label-${season.id}`}
              value={ui.selectedCategory}
              label="Categoria"
              onChange={(event) =>
                onUpdateSeasonUi(
                  season.id,
                  "selectedCategory",
                  event.target.value,
                )
              }
            >
              <MenuItem value="">Todas</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.name} - {TYPE_LABELS[cat.type] ?? cat.type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            size="small"
            label="Filtrar jogador"
            value={ui.searchPlayer}
            onChange={(event) =>
              onUpdateSeasonUi(season.id, "searchPlayer", event.target.value)
            }
          />
        </Grid>
      </Grid>

      {seasonResult.loading ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Carregando jogos...</Typography>
        </Stack>
      ) : lines.length === 0 ? (
        <EmptyState
          icon={<SportsTennisIcon sx={{ fontSize: 34 }} />}
          title="Nenhuma linha de jogos encontrada"
          description="Nenhuma linha de jogos encontrada para esse filtro."
          sx={{ py: 3 }}
        />
      ) : (
        <Stack spacing={1.2}>
          {[...lines]
            .map((line, i) => ({ ...line, _originalNumber: i + 1 }))
            .sort((a, b) => {
              const aHasPending = a.games.some((g) => !g.finished);
              const bHasPending = b.games.some((g) => !g.finished);
              return aHasPending === bHasPending ? 0 : aHasPending ? -1 : 1;
            })
            .map((line) => {
              const lineSent = Boolean(sentLines[line.lineId]);
              const sortedGames = [...line.games].sort((a, b) =>
                a.finished === b.finished ? 0 : a.finished ? 1 : -1,
              );
              return (
                <Paper
                  key={line.lineId}
                  elevation={0}
                  sx={{
                    borderRadius: 1.5,
                    p: 0.8,
                  }}
                >
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    justifyContent="space-between"
                    spacing={0.8}
                    sx={{ mb: 1 }}
                  >
                    {lineSent && (
                      <Chip
                        color="success"
                        size="small"
                        label="Resultados enviados"
                        sx={{ fontWeight: 700 }}
                      />
                    )}
                  </Stack>

                  <Grid container spacing={1}>
                    {sortedGames.map((game) => {
                      const draft = lineDrafts[line.lineId]?.[game.gameId] || {
                        teamA: "",
                        teamB: "",
                      };
                      return (
                        <Grid item xs={12} md={4} key={game.gameId}>
                          <Paper
                            elevation={0}
                            sx={{
                              border: "2px solid",
                              borderColor: game.finished
                                ? "success.main"
                                : "divider",
                              borderRadius: 1,
                              p: 1,
                            }}
                          >
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns:
                                  "minmax(0, 1fr) auto minmax(0, 1fr)",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  noWrap
                                  sx={{ fontWeight: 700, fontSize: 13 }}
                                >
                                  {game.teamA[0]}
                                </Typography>
                                <Typography
                                  noWrap
                                  sx={{ fontWeight: 700, fontSize: 13 }}
                                >
                                  {game.teamA[1]}
                                </Typography>
                              </Box>

                              <Stack
                                direction="row"
                                spacing={0.5}
                                alignItems="center"
                              >
                                <TextField
                                  size="small"
                                  value={draft.teamA}
                                  onChange={(event) =>
                                    onScoreChange(
                                      line.lineId,
                                      game.gameId,
                                      "teamA",
                                      event.target.value,
                                    )
                                  }
                                  disabled={!season.isActive}
                                  inputProps={{
                                    inputMode: "numeric",
                                    style: {
                                      textAlign: "center",
                                      padding: "6px",
                                    },
                                  }}
                                  sx={{ width: 52 }}
                                />
                                <Typography sx={{ fontWeight: 800 }}>
                                  x
                                </Typography>
                                <TextField
                                  size="small"
                                  value={draft.teamB}
                                  onChange={(event) =>
                                    onScoreChange(
                                      line.lineId,
                                      game.gameId,
                                      "teamB",
                                      event.target.value,
                                    )
                                  }
                                  disabled={!season.isActive}
                                  inputProps={{
                                    inputMode: "numeric",
                                    style: {
                                      textAlign: "center",
                                      padding: "6px",
                                    },
                                  }}
                                  sx={{ width: 52 }}
                                />
                              </Stack>

                              <Box sx={{ minWidth: 0, textAlign: "right" }}>
                                <Typography
                                  noWrap
                                  sx={{ fontWeight: 700, fontSize: 13 }}
                                >
                                  {game.teamB[0]}
                                </Typography>
                                <Typography
                                  noWrap
                                  sx={{ fontWeight: 700, fontSize: 13 }}
                                >
                                  {game.teamB[1]}
                                </Typography>
                              </Box>
                            </Box>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>

                  <Box
                    sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}
                  >
                    {season.isActive && lineHasChanges(line.lineId, line) && (
                      <Button
                        variant="contained"
                        size="small"
                        disabled={!canSendLine(line.lineId, line)}
                        onClick={() => onSendLine(line.lineId, line)}
                        sx={{ textTransform: "none", fontWeight: 700 }}
                      >
                        Enviar resultados da linha
                      </Button>
                    )}
                  </Box>
                </Paper>
              );
            })}
        </Stack>
      )}
    </AccordionDetails>
  </Accordion>
);

export default SeasonAccordion;
